import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
	AbstractControl,
	AbstractControlOptions,
	AsyncValidatorFn,
	FormBuilder,
	FormControl,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { formInvalidOperator } from '@nutrition/rations/my-rations/ration-details/ration-details.utils';
import { FormControlStatus } from '@shared/model/form-control-status.enum';
import { RegexPatterns } from '@shared/validators/regex-patterns.enum';
import { BehaviorSubject, EMPTY, Observable, Subject, timer } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { ConfirmedValidator } from '../utils';

@Component({
	selector: 'sf-sign-up',
	templateUrl: './sign-up.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignUpComponent implements OnDestroy {
	public form = this.fb.group(
		{
			email: ['', [Validators.required, Validators.pattern(RegexPatterns.EMAIL)], [this.usernameValidator()]],
			password: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(RegexPatterns.PASSWORD)]],
			passwordRepeat: ['', [Validators.required, Validators.maxLength(50)]],
			policyAndTerms: [false, [Validators.requiredTrue]],
			newsletter: [false],
		},
		{ validators: [ConfirmedValidator('password', 'passwordRepeat')] } as AbstractControlOptions
	);
	public isConfirmationVisible: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public showNewsletterInfo: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public loading: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public FormControlStatus: typeof FormControlStatus = FormControlStatus;
	public formInvalid$: Observable<boolean> = this.form.statusChanges.pipe(
		formInvalidOperator(this.form.status as FormControlStatus)
	);

	private destroy: Subject<void> = new Subject();

	public get emailControl(): FormControl<string> {
		return this.form.controls.email;
	}

	public get passwordRepeatControl(): FormControl<string> {
		return this.form.controls.passwordRepeat;
	}

	public get newsletterControl(): FormControl<boolean> {
		return this.form.controls.newsletter;
	}

	public get policyAndTermsControl(): FormControl<boolean> {
		return this.form.controls.policyAndTerms;
	}

	constructor(
		private fb: FormBuilder,
		private authService: AuthService
	) {}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	public onPasswordBlur(): void {
		if (this.passwordRepeatControl.dirty) {
			this.passwordRepeatControl.updateValueAndValidity();
			this.passwordRepeatControl.markAsTouched();
		}
	}

	public onCreateAccount(): void {
		this.loading.next(true);
		// eslint-disable-next-line
		const { policyAndTerms, ...form } = this.form.getRawValue();
		this.authService
			.createAccount({ ...form, referrer: document.referrer })
			.pipe(
				catchError(() => {
					this.loading.next(false);
					return EMPTY;
				}),
				tap(() => {
					this.loading.next(false);
					this.isConfirmationVisible.next(true);
				}),
				takeUntil(this.destroy)
			)
			.subscribe();
	}

	private usernameValidator(): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			return timer(300).pipe(
				switchMap(() =>
					this.authService.isEmailTaken(control.value).pipe(map((res) => (res ? { usernameExists: true } : null)))
				)
			);
		};
	}

	public showMore(event: Event): void {
		this.showNewsletterInfo.next(!this.showNewsletterInfo.value);
		event.stopPropagation();
		event.preventDefault();
	}
}
